import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';

import { TooltipContentPlacement, TooltipType } from '../../types';
import { getCoords, getDegreeOfVisibility } from './helpers';

import styles from './Tooltip.module.scss';

type Props = TooltipType & {
    frameIndex: number;
    framesMaxIndex: number;
    setFrame?: (frame: number) => void;
    hideWhileSpinning?: boolean;
    changeVisibilityNearExtremeFrames?: boolean;
    hidden?: boolean;
    occlude?: boolean;
};

const Tooltip: React.FC<Props> = ({
    text,
    coords,
    contentPlacement = TooltipContentPlacement.Bottom,
    sizes,
    hideWhileSpinning = true,
    changeVisibilityNearExtremeFrames = false,
    frameIndex,
    framesMaxIndex,
    setFrame,
    hidden,
    occlude = true,
}) => {
    const hiddenAfterSpinningTimeoutRef = useRef<NodeJS.Timeout>(setTimeout(() => {}, 0));

    const [isOpen, setIsOpen] = useState(false);

    const [isHiddenAfterSpinning, setIsHiddenAfterSpinning] = useState<boolean>(false);
    const [degreeOfVisibility, setDegreeOfVisibility] = useState(5);

    const currentCoords = useMemo(
        () => getCoords(coords, framesMaxIndex, frameIndex),
        [coords, framesMaxIndex, frameIndex],
    );

    const onClick = useCallback(() => {
        if (sizes && setFrame) setFrame(sizes.frameIndex);
    }, [sizes, setFrame]);

    useEffect(() => {
        if (hideWhileSpinning) {
            clearTimeout(hiddenAfterSpinningTimeoutRef.current);
            hiddenAfterSpinningTimeoutRef.current = setTimeout(
                () => setIsHiddenAfterSpinning(false),
                200,
            );

            setIsHiddenAfterSpinning(true);
        }
    }, [frameIndex, setIsHiddenAfterSpinning]);

    useEffect(() => {
        if (!occlude) return;

        if ((hideWhileSpinning && isHiddenAfterSpinning) || hidden) {
            setDegreeOfVisibility(0);
            return;
        }

        const frameIndexes: number[] = [];
        for (const frame of coords.keys()) {
            frameIndexes[frameIndexes.length] = frame;
        }

        const firstFrameIndex = frameIndexes[0];
        const lastFrameIndex = frameIndexes[frameIndexes.length - 1];
        const isFirstFrameIndexSmallerThanLast = firstFrameIndex < lastFrameIndex;

        const isVisible = isFirstFrameIndexSmallerThanLast
            ? frameIndex >= firstFrameIndex && frameIndex <= lastFrameIndex
            : frameIndex <= lastFrameIndex || frameIndex >= firstFrameIndex;

        if (!isVisible) {
            setDegreeOfVisibility(0);
            return;
        }

        if (!changeVisibilityNearExtremeFrames || hideWhileSpinning) {
            setDegreeOfVisibility(isVisible ? 5 : 0);
            return;
        }

        const degreeOfVisibility = getDegreeOfVisibility(
            firstFrameIndex,
            lastFrameIndex,
            framesMaxIndex,
            frameIndex,
        );

        setDegreeOfVisibility(degreeOfVisibility);
    }, [
        occlude,
        hidden,
        frameIndex,
        setDegreeOfVisibility,
        coords,
        framesMaxIndex,
        sizes,
        hideWhileSpinning,
        isHiddenAfterSpinning,
        changeVisibilityNearExtremeFrames,
    ]);

    return (
        <button
            className={classnames(styles.Tooltip, styles[`visibilityMode-${degreeOfVisibility}`])}
            style={{ top: `${currentCoords.y}%`, left: `${currentCoords.x}%` }}
            onMouseOver={() => setIsOpen(true)}
            onMouseOut={() => setIsOpen(false)}
            onClick={onClick}
        >
            <span />
            <div
                className={classnames(styles.Tooltip__content, styles[contentPlacement], {
                    [styles.visible]: isOpen,
                })}
            >
                <p>{text}</p>
                <div className={classnames(styles.content__arrow, styles[contentPlacement])} />
            </div>
        </button>
    );
};

export { Tooltip };
